/**
 * Debtors API client
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface CreateDebtorRequest {
  company_name: string;
  company_number: string;
  country_code: string;
  contact_email?: string;
  phone?: string;
  address?: string;
  payment_terms_days?: number;
}

export interface DebtorResponse {
  id: string;
  company_name: string;
  company_number: string;
  country_code: string;
  contact_email?: string;
  phone?: string;
  address?: string;
  payment_terms_days: number;
  grade: number | null;
  risk_rating: number | null;
  last_credit_check?: string;
  last_credit_check_result: string;
  status: string;
  dekking?: number | null;
  verzekerbaar?: boolean;
}

export interface DebtorListResponse {
  debtors: DebtorResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface DebtorSearchResponse {
  debtors: DebtorResponse[];
}

export async function createDebtor(
  token: string,
  request: CreateDebtorRequest
): Promise<DebtorResponse> {
  const response = await fetch(`${API_BASE_URL}/api/debtors`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create debtor');
  }

  return response.json();
}

export async function getDebtor(
  token: string,
  debtorId: string
): Promise<DebtorResponse> {
  const response = await fetch(`${API_BASE_URL}/api/debtors/${debtorId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch debtor');
  }

  return response.json();
}

export async function listDebtors(
  token: string,
  options?: {
    grade?: string;
    skip?: number;
    limit?: number;
  }
): Promise<DebtorListResponse> {
  const params = new URLSearchParams();
  if (options?.grade) params.append('grade', options.grade);
  if (options?.skip) params.append('skip', options.skip.toString());
  if (options?.limit) params.append('limit', options.limit.toString());

  const url = new URL(`${API_BASE_URL}/api/debtors`);
  url.search = params.toString();

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to list debtors');
  }

  return response.json();
}

export async function searchDebtors(
  token: string,
  query: string
): Promise<DebtorSearchResponse> {
  const params = new URLSearchParams({ q: query });
  const response = await fetch(
    `${API_BASE_URL}/api/debtors/search?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to search debtors');
  }

  return response.json();
}
